import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import * as blueprint from '@adc/blueprint'
import coreLogger from '@adc/logger'
import type {
  BlueprintCognitionData,
  BlueprintCognitionActions,
} from '@adc/blueprint'
import type {
  CognitionEvaluationChatMessage,
  CognitionEvaluationGroup,
  CognitionEvaluationGroupParams,
  CognitionEvaluationGroupStatus,
  CognitionEvaluationMethod,
  CognitionEvaluationTaskName,
  CognitionEvaluationContextInput,
  CognitionEvaluationScore,
  URN,
} from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import AiChat, { AiChatProps, AiChatEvalGroupUpdateHandler } from './'
import type { MarkdownComponentAction } from '../elementals/markdownWithComponents/types'
import type { AutomationTarget } from '../../types'

const logger = coreLogger.forModule('UICORE:AICHAT')

const ModelRole = graph.enums.CognitionEvaluationRole.Model
const UserRole = graph.enums.CognitionEvaluationRole.User

const TROUBLE_CONNECTING_MESSAGE =
  "I apologize, but I'm having trouble connecting. Please try again momentarily."

const AUTHENTICATION_ERROR_MESSAGE =
  'This chat has ended. Thank you for using the service.'

// Internal component that wraps the Blueprint hook
// This component remounts when internalResetKey changes, clearing the hook's collection
interface AiChatContainerInternalProps {
  graphWebsocket: blueprint.primitives.IGraphWebsocket
  taskName: CognitionEvaluationTaskName
  user: blueprint.primitives.AuctionUser
  blueprintContext?: CognitionEvaluationContextInput
  onResetComplete?: () => void
  className?: string
  context?: AiChatProps['context']
  onFeedback?: AiChatProps['onFeedback']
  onCopy?: (content: string) => void
  feedbackPromptTextPositive?: React.ReactNode
  feedbackPromptTextNegative?: React.ReactNode
  feedbackPromptMoreHelpText?: React.ReactNode
  externalPrompt?: string
  onExternalPromptProcessed?: () => void
  onSend?: AiChatProps['onSend']
  onAction?: (action: MarkdownComponentAction) => void
  showContext?: boolean
  showContextAsLink?: boolean
  hideChatFeedback?: boolean
  showProgress?: boolean
  showPrivateMessages?: boolean
  introMessage?: string
  open?: boolean
  elmId?: string
  initialEvaluationGroupUrn?: URN
  onEvalGroupUpdate?: AiChatEvalGroupUpdateHandler
  onConversationStart?: (conversationUrn: URN) => void
  enableLiveAgentBanner?: boolean
  onLiveAgentClick?: () => void
  onLiveAgentBannerDisplayed?: () => void
}

const AiChatContainerInternal: React.FC<AiChatContainerInternalProps> = ({
  graphWebsocket,
  taskName,
  blueprintContext,
  className,
  context,
  onFeedback,
  onCopy,
  feedbackPromptTextPositive,
  feedbackPromptTextNegative,
  feedbackPromptMoreHelpText,
  externalPrompt,
  onExternalPromptProcessed,
  onSend,
  onAction,
  showContext,
  showContextAsLink,
  hideChatFeedback,
  showProgress = true,
  showPrivateMessages = false,
  introMessage,
  open,
  elmId,
  initialEvaluationGroupUrn,
  onEvalGroupUpdate,
  onConversationStart,
  user,
  enableLiveAgentBanner = false,
  onLiveAgentClick,
  onLiveAgentBannerDisplayed,
}) => {
  const [messages, setMessages] = useState<CognitionEvaluationChatMessage[]>([])
  const [isAwaitingReply, setIsAwaitingReply] = useState(false)
  const [evaluationGroup, setEvaluationGroup] = useState<
    CognitionEvaluationGroup | undefined
  >(undefined)
  const [failedToGetLastReply, setFailedToGetLastReply] = useState(false)
  const [isAuthError, setIsAuthError] = useState(false)
  const [isLoading, setIsLoading] = useState(!!initialEvaluationGroupUrn)
  const evaluationGroupUrnRef = useRef<URN | undefined>(
    initialEvaluationGroupUrn || undefined
  )
  const evaluationMethodRef = useRef<CognitionEvaluationMethod | undefined>(
    undefined
  )
  const previousStatusRef = useRef<CognitionEvaluationGroupStatus | undefined>(
    undefined
  )
  const previousMessageCountRef = useRef<number>(0)
  const hasInitialLoadFiredRef = useRef<boolean>(false)

  const evaluationGroups = useMemo(() => {
    // If we have an initial URN, use it to load existing chat
    if (initialEvaluationGroupUrn) {
      return [
        {
          urn: initialEvaluationGroupUrn,
        } as CognitionEvaluationGroup,
      ]
    }
    // Otherwise, use the evaluationGroup state if it exists (current behavior)
    return evaluationGroup ? [evaluationGroup] : []
  }, [initialEvaluationGroupUrn, evaluationGroup])

  const handleCognitionError = useCallback(
    (error: Error) => {
      logger.error('Cognition error:', error)
      setIsAwaitingReply(false)

      setMessages(prevMessages => {
        // Determine error message based on context
        let errorMessage = TROUBLE_CONNECTING_MESSAGE

        // On 403 unauthenticated error, show login message and disable prompting
        if (blueprint.utils.isAnyAuthenticationError(error)) {
          errorMessage = AUTHENTICATION_ERROR_MESSAGE
          setIsAuthError(true)
        }

        // If we failed to load initial evaluation group, show specific message
        if (initialEvaluationGroupUrn && prevMessages.length === 0) {
          errorMessage =
            'Failed to load the prior chat, however you can start a new one if you like.'
        }

        const lastMessage = prevMessages[prevMessages.length - 1]

        // Don't duplicate error messages
        if (lastMessage?.object?.message === errorMessage) {
          return prevMessages
        }

        return [
          ...prevMessages,
          {
            object: {
              message: errorMessage,
            },
            role: ModelRole,
            sequence: prevMessages.length + 1,
            type: 'object',
            __typename: 'CognitionEvaluationChatOutputMessage',
          },
        ]
      })
    },
    [initialEvaluationGroupUrn]
  )

  const handleEvalGroupUpdate = useCallback(
    (evaluationGroup: CognitionEvaluationGroup | undefined) => {
      if (onEvalGroupUpdate) {
        onEvalGroupUpdate(evaluationGroup)
      }
    },
    [onEvalGroupUpdate]
  )

  // eslint-disable-next-line
  const {
    data,
    actions,
  }: blueprint.primitives.AuctionBlueprintHook<
    BlueprintCognitionData,
    BlueprintCognitionActions
  > = blueprint.cognition.hooks.useCognition({
    enabled: true,
    graphWebsocket,
    initialData: {
      evaluationGroups,
    },
    evaluationFilters: {
      is_winner: true,
    },
    task: taskName,
    user,
    onError: handleCognitionError,
  })

  // Initialize ref if starting with an existing evaluation group
  useEffect(() => {
    if (initialEvaluationGroupUrn) {
      setIsLoading(true)
      setMessages([])
      evaluationGroupUrnRef.current = initialEvaluationGroupUrn
      evaluationMethodRef.current = undefined
      // Reset tracking refs when loading a new evaluation group
      previousStatusRef.current = undefined
      previousMessageCountRef.current = 0
      hasInitialLoadFiredRef.current = false
    }
  }, [initialEvaluationGroupUrn])

  useEffect(() => {
    if (data.evaluationGroups && evaluationGroupUrnRef.current) {
      const evaluationGroup = data.evaluationGroups.mapped.get(
        evaluationGroupUrnRef.current
      )
      if (evaluationMethodRef.current === undefined)
        evaluationMethodRef.current =
          evaluationGroup?.__methods__.getWinnerEvaluationMethod()

      const stitchedMessages = evaluationGroup?.__methods__.getMessages({
        includeProgress: showProgress,
        includePrivateMessages: showPrivateMessages,
      })[
        evaluationMethodRef.current ||
          graph.enums.CognitionEvaluationMethod.InitialMethod
      ] as CognitionEvaluationChatMessage[]
      // Only set isLoading=false if we have valid message data (not undefined)
      if (stitchedMessages !== undefined) {
        setMessages(stitchedMessages)
        setIsLoading(false)
      }

      // Track changes for onEvalGroupUpdate callback
      const currentStatus = evaluationGroup?.status
      const currentMessageCount = stitchedMessages?.length || 0
      const previousStatus = previousStatusRef.current
      const previousMessageCount = previousMessageCountRef.current

      let shouldFireCallback = false

      // Initial load case: Fire once when we first get messages
      if (
        !hasInitialLoadFiredRef.current &&
        currentMessageCount > 0 &&
        evaluationGroup
      ) {
        shouldFireCallback = true
        hasInitialLoadFiredRef.current = true
      }
      // Status change detected
      else if (currentStatus && currentStatus !== previousStatus) {
        shouldFireCallback = true
      }
      // New messages detected (but not on initial load)
      else if (
        hasInitialLoadFiredRef.current &&
        currentMessageCount > previousMessageCount
      ) {
        shouldFireCallback = true
      }

      // Update refs for next comparison
      previousStatusRef.current = currentStatus
      previousMessageCountRef.current = currentMessageCount

      // Fire callback if conditions met
      if (shouldFireCallback) {
        handleEvalGroupUpdate(evaluationGroup)
      }

      if (stitchedMessages?.length > 0) {
        const lastMessage = stitchedMessages[stitchedMessages.length - 1]
        const statusIsCompleted =
          evaluationGroup?.status ===
          graph.enums.CognitionEvaluationGroupStatus.Completed

        // When private messages are shown, use the last non-progress message
        // to determine awaiting state. Progress messages (which have a
        // 'description' field and Model role) should not re-enable the prompt;
        // only a real output should.
        const isProgressMsg = (msg: CognitionEvaluationChatMessage): boolean =>
          'description' in msg &&
          !!(msg as unknown as { description: string }).description

        const lastNonProgressMessage = showPrivateMessages
          ? [...stitchedMessages].reverse().find(msg => !isProgressMsg(msg))
          : lastMessage

        const lastMessageIsNotModelRole = lastNonProgressMessage
          ? lastNonProgressMessage.role !== ModelRole
          : lastMessage.role !== ModelRole

        const awaitingBasedOnRole = lastMessageIsNotModelRole

        // Only flag as failed if status is COMPLETED and the last input
        // sequence has been fully processed (has a matching output sequence).
        // If the last input sequence is ahead of the last output sequence,
        // the server is still processing — not failed.
        const lastInputSequence = Math.max(
          0,
          ...(evaluationGroup?.inputs?.map(i => i.sequence || 0) || [])
        )
        const lastOutputSequence = Math.max(
          0,
          ...(evaluationGroup?.evaluations?.flatMap(
            e => e.outputs?.map(o => o.sequence || 0) || []
          ) || [])
        )
        const serverHasProcessedLastInput =
          lastOutputSequence >= lastInputSequence

        if (
          statusIsCompleted &&
          lastMessageIsNotModelRole &&
          serverHasProcessedLastInput
        ) {
          setIsAwaitingReply(false)
          setFailedToGetLastReply(true)
        } else {
          setFailedToGetLastReply(false)
          setIsAwaitingReply(awaitingBasedOnRole && !statusIsCompleted)
        }
      } else {
        // No messages yet
        setFailedToGetLastReply(false)
      }
    }
  }, [data, showProgress, handleEvalGroupUpdate])

  const displayMessages = useMemo(() => {
    if (failedToGetLastReply) {
      return [
        ...messages,
        {
          object: {
            message:
              'My apologies. I failed to receive the last response from the server. Please start a new chat or reload the page.',
          },
          role: ModelRole,
          sequence: messages.length + 1,
          type: 'object',
          __typename: 'CognitionEvaluationChatOutputMessage' as const,
        },
      ]
    }
    return messages
  }, [messages, failedToGetLastReply])

  const upsertEvaluationGroup = async (
    content: CognitionEvaluationGroupParams['content']
  ): Promise<void> => {
    setIsAwaitingReply(true)
    setFailedToGetLastReply(false)
    try {
      if (!evaluationGroupUrnRef.current) {
        // first message, create new evaluation group
        setMessages([
          {
            object: {
              message: content,
            },
            role: graph.enums.CognitionEvaluationRole.User,
            sequence: 0,
            type: 'object',
            __typename: 'CognitionEvaluationChatInputMessage',
          },
        ])
        const response = await actions.createEvaluationGroup({
          task: taskName,
          content,
          context: blueprintContext,
        })
        if (response) {
          setEvaluationGroup(response)
          evaluationGroupUrnRef.current = response.urn
          setIsAwaitingReply(false)
          // A new evaluation group == a new conversation. Fire exactly once
          // here (not on open, not when resuming via initialEvaluationGroupUrn)
          // so consumers can track unique conversations.
          onConversationStart?.(response.urn)
        }
      } else {
        actions.upsertEvaluationGroup(
          {
            task: taskName,
            content,
            context: blueprintContext,
          },
          evaluationGroupUrnRef.current
        )
      }
    } catch (error) {
      logger.error('Failed to upsert evaluation group:', error)
      setIsAwaitingReply(false)

      setMessages(prevMessages => [
        ...prevMessages,
        {
          object: {
            message:
              "I apologize, but I'm having trouble processing your request. Please try again.",
          },
          role: ModelRole,
          sequence: prevMessages.length + 1,
          type: 'object',
          __typename: 'CognitionEvaluationChatOutputMessage',
        },
      ])
      return
    }

    setMessages(prevMessages => [
      ...prevMessages,
      {
        object: {
          message: content,
        },
        role: UserRole,
        sequence: prevMessages.length,
        type: 'object',
        __typename: 'CognitionEvaluationChatInputMessage',
      },
    ])
  }

  // Create wrapper for onCopy to allow consumer callback chaining
  const handleCopy = (content: string) => {
    // The CopyButton component already handles the copy operation internally
    // We just need to call the consumer's callback if provided
    if (onCopy) {
      onCopy(content)
    }
  }

  /**
   * Handles feedback submission by saving it to the backend and optionally notifying the consumer.
   * Note that "score" here refers to the CognitionEvaluationScore provided by the AiChat component, which looks like:
   * { score: boolean; notes?: string }, i.e. so it's score.score is a boolean indicating thumbs up/down
   */
  const handleFeedback: AiChatProps['onFeedback'] = useCallback(
    async (score: CognitionEvaluationScore) => {
      // save to backend
      await actions.scoreEvaluation({
        urn: evaluationGroupUrnRef.current as URN,
        method: evaluationMethodRef.current as CognitionEvaluationMethod,
        score,
      })
      // optionally tell consumer as well
      if (onFeedback) {
        return onFeedback(score)
      }
      return true
    },
    [actions, onFeedback]
  )

  /**
   * Handles interactive markdown component actions.
   * If the action contains a `prompt` string, it is automatically submitted
   * as the user's next message. The consumer's onAction callback is always
   * called first for analytics/logging.
   */
  const handleAction = useCallback(
    (mdAction: MarkdownComponentAction) => {
      // Notify consumer first
      if (onAction) {
        onAction(mdAction)
      }

      // Auto-submit prompt-based actions as user messages
      if (typeof mdAction.prompt === 'string' && mdAction.prompt.trim()) {
        upsertEvaluationGroup(mdAction.prompt)
      }
    },
    [onAction, upsertEvaluationGroup]
  )

  // Show banner only after AI has responded to a user message with a real answer
  // (not the intro message, not progress/"Thinking..." messages)
  const hasAiRespondedToUser = messages.some(
    (msg, idx) =>
      msg.role === ModelRole &&
      idx > 0 &&
      'object' in msg &&
      !!msg.object?.message &&
      messages.slice(0, idx).some(m => m.role === UserRole)
  )

  return (
    <AiChat
      upsertEvaluationGroup={upsertEvaluationGroup}
      messages={displayMessages}
      isAwaitingReply={isAwaitingReply}
      disallowPrompting={failedToGetLastReply || isAuthError}
      isLoading={isLoading}
      className={className}
      context={context}
      onFeedback={handleFeedback}
      onCopy={handleCopy}
      feedbackPromptTextPositive={feedbackPromptTextPositive}
      feedbackPromptTextNegative={feedbackPromptTextNegative}
      feedbackPromptMoreHelpText={feedbackPromptMoreHelpText}
      externalPrompt={externalPrompt}
      onExternalPromptProcessed={onExternalPromptProcessed}
      showContext={showContext}
      showContextAsLink={showContextAsLink}
      hideChatFeedback={hideChatFeedback}
      introMessage={introMessage}
      open={open}
      elmId={elmId}
      onSend={onSend}
      onAction={handleAction}
      showLiveAgentBanner={enableLiveAgentBanner && hasAiRespondedToUser}
      onLiveAgentClick={onLiveAgentClick}
      onLiveAgentBannerDisplayed={onLiveAgentBannerDisplayed}
    />
  )
}

export interface AiChatContainerProps
  extends AutomationTarget,
    Pick<
      AiChatProps,
      | 'className'
      | 'context'
      | 'externalPrompt'
      | 'feedbackPromptTextPositive'
      | 'feedbackPromptTextNegative'
      | 'feedbackPromptMoreHelpText'
      | 'hideChatFeedback'
      | 'introMessage'
      | 'onCopy'
      | 'onExternalPromptProcessed'
      | 'onSend'
      | 'onFeedback'
      | 'onAction'
      | 'showContext'
      | 'showContextAsLink'
      | 'open'
    > {
  graphWebsocket: blueprint.primitives.IGraphWebsocket
  taskName: CognitionEvaluationTaskName
  showProgress?: boolean
  showPrivateMessages?: boolean
  resetKey?: number | string
  onResetComplete?: () => void
  initialEvaluationGroupUrn?: URN
  onEvalGroupUpdate?: AiChatEvalGroupUpdateHandler
  /** Fires once when a new conversation (evaluation group) is created */
  onConversationStart?: (conversationUrn: URN) => void
  user: blueprint.primitives.AuctionUser
  /** URN to impersonate when running the evaluation (e.g. adc:email:user@example.com or adc:anonymous_session:abc123) */
  runAs?: URN
  /** Whether the live agent banner feature is enabled (flag + business hours) */
  enableLiveAgentBanner?: boolean
  /** Callback when live agent banner is clicked */
  onLiveAgentClick?: () => void
  /** Callback when live agent banner is displayed (for analytics) */
  onLiveAgentBannerDisplayed?: () => void
}

const AiChatContainer: React.FC<AiChatContainerProps> = ({
  graphWebsocket,
  taskName,
  className,
  context,
  onFeedback,
  onCopy,
  feedbackPromptTextPositive,
  feedbackPromptTextNegative,
  feedbackPromptMoreHelpText,
  externalPrompt,
  onExternalPromptProcessed,
  onSend,
  onAction,
  showContext,
  showContextAsLink,
  hideChatFeedback,
  showProgress = true,
  showPrivateMessages = false,
  introMessage,
  open,
  elmId,
  resetKey,
  onResetComplete,
  initialEvaluationGroupUrn,
  onEvalGroupUpdate,
  onConversationStart,
  user,
  runAs,
  enableLiveAgentBanner = false,
  onLiveAgentClick,
  onLiveAgentBannerDisplayed,
}) => {
  const [blueprintContext, setBlueprintContext] = useState<
    CognitionEvaluationContextInput | undefined
  >(undefined)
  const [internalResetKey, setInternalResetKey] = useState(0)

  const handleReset = useCallback(() => {
    // Increment internal reset key to remount the internal component
    // This will clear the Blueprint hook's collection
    setInternalResetKey(prev => prev + 1)

    // Notify consumer
    onResetComplete?.()
  }, [onResetComplete])

  useEffect(() => {
    setBlueprintContext(
      context && context.length > 0
        ? {
            sources: context
              .map(item => item.urn)
              .filter((urn): urn is string => urn !== undefined),
            ...(runAs ? { run_as: runAs } : {}),
          }
        : undefined
    )
  }, [context, runAs])

  // Watch for resetKey prop changes
  const lastResetKeyRef = useRef(resetKey)
  useEffect(() => {
    if (resetKey !== undefined && resetKey !== lastResetKeyRef.current) {
      lastResetKeyRef.current = resetKey
      handleReset()
    }
  }, [resetKey, handleReset])

  return (
    <div
      data-elm-id={elmId ? `${elmId}_container` : undefined}
      style={{ height: '100%' }}
    >
      <AiChatContainerInternal
        key={internalResetKey}
        graphWebsocket={graphWebsocket}
        taskName={taskName}
        blueprintContext={blueprintContext}
        className={className}
        context={context}
        onFeedback={onFeedback}
        onCopy={onCopy}
        feedbackPromptTextPositive={feedbackPromptTextPositive}
        feedbackPromptTextNegative={feedbackPromptTextNegative}
        feedbackPromptMoreHelpText={feedbackPromptMoreHelpText}
        externalPrompt={externalPrompt}
        onExternalPromptProcessed={onExternalPromptProcessed}
        onSend={onSend}
        onAction={onAction}
        showContext={showContext}
        showContextAsLink={showContextAsLink}
        hideChatFeedback={hideChatFeedback}
        showProgress={showProgress}
        showPrivateMessages={showPrivateMessages}
        introMessage={introMessage}
        open={open}
        elmId={elmId}
        initialEvaluationGroupUrn={initialEvaluationGroupUrn}
        onEvalGroupUpdate={onEvalGroupUpdate}
        onConversationStart={onConversationStart}
        user={user}
        enableLiveAgentBanner={enableLiveAgentBanner}
        onLiveAgentClick={onLiveAgentClick}
        onLiveAgentBannerDisplayed={onLiveAgentBannerDisplayed}
      />
    </div>
  )
}

export default AiChatContainer
